import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import dayjs, { Dayjs } from "dayjs";
import { addMonths } from "date-fns";
import * as XLSX from "xlsx";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Assessment, ShowChart, Upload } from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import Toast from "../../components/Layout/Toast";
import { selectPreference } from "../preferences/preferencesSlice";
import { fetchGovernmentReport, selectInvoices } from "./invoiceSlices";
import { SectionCard } from "./components/SectionCard";

export const GovernmentReport = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { locale } = useAppSelector(selectPreference);
  const { governmentReport, loading, error } = useAppSelector(selectInvoices);

  const [startDate, setStartDate] = useState<Date | null>(
    addMonths(new Date(), -1),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    setShowReport(false);
  }, [startDate, endDate]);

  const isFetching = loading === "pending";

  const handleShow = () => {
    if (!startDate || !endDate) return;
    dispatch(fetchGovernmentReport({ startDate, endDate }));
    setShowReport(true);
  };

  const handleExport = () => {
    if (!governmentReport) return;
    const rows = governmentReport.employees.map((employee) => ({
      fullName: `${employee.givenName ?? ""} ${employee.surName ?? ""}`.trim(),
      passportNumber: employee.passportNumber,
      country: te((employee.additionalData?.appliedCountry as string) ?? ""),
      agent: employee.partner?.displayName,
      departedOn: employee.statusDates?.departed
        ? format(
            new Date(employee.statusDates.departed),
            "dd-MMM-yyyy",
          ).toUpperCase()
        : "",
      reportedAmount: employee.agentSettlement?.reportedAmount ?? 0,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "GovernmentReport");
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [
          "Full Name",
          "Passport No.",
          "Country",
          "Agent",
          "Departed On",
          "Reported Amount (USD)",
        ],
      ],
      { origin: "A1" },
    );
    XLSX.writeFile(
      workbook,
      new Date().toLocaleDateString() + "_GovernmentReport.xlsx",
      { compression: true },
    );
  };

  return (
    <>
      <Helmet>
        <title>{`Government Report | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <SectionCard icon={<Assessment />} title="Government Remittance Report">
            <Typography color="textSecondary" sx={{ mb: 1 }}>
              All employees departed within the date range (inclusive), with
              the reported amounts — independent of invoicing.
            </Typography>
            <Grid container spacing={2} alignItems="center">
            <Grid item md={4} xs={12}>
              <DatePicker
                label={t("fromDate")}
                disableMaskedInput
                views={["year", "month", "day"]}
                inputFormat="DD-MMM-YYYY"
                value={startDate ? dayjs(startDate) : null}
                onChange={(newValue: Dayjs | null) =>
                  setStartDate(newValue ? newValue.toDate() : null)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <DatePicker
                label={t("toDate")}
                disableMaskedInput
                views={["year", "month", "day"]}
                inputFormat="DD-MMM-YYYY"
                value={endDate ? dayjs(endDate) : null}
                onChange={(newValue: Dayjs | null) =>
                  setEndDate(newValue ? newValue.toDate() : null)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <Stack direction="row" spacing={1}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleShow}
                  disabled={isFetching || !startDate || !endDate}
                >
                  {isFetching ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <ShowChart /> {t("show")}
                    </>
                  )}
                </Button>
                {showReport && governmentReport && (
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={handleExport}
                  >
                    <Upload /> Export to Excel
                  </Button>
                )}
              </Stack>
            </Grid>
            </Grid>
          </SectionCard>
        </LocalizationProvider>

        {error && <Toast severity="error">{error.message}</Toast>}

        {showReport && !isFetching && governmentReport && (
          <>
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                Totals by {t("country")}
              </Typography>
              <Table size="small" aria-label="country totals table">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>{t("country")}</StyledTableCell>
                    <StyledTableCell align="right">Employees</StyledTableCell>
                    <StyledTableCell align="right">
                      Reported Total (USD)
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {governmentReport.countryTotals.map((row) => (
                    <StyledTableRow key={row.country}>
                      <StyledTableCell>{te(row.country)}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.count}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.total.toLocaleString()}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                  <StyledTableRow>
                    <StyledTableCell>
                      <b>Grand Total</b>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <b>{governmentReport.totalCount}</b>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <b>{governmentReport.totalReported.toLocaleString()}</b>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              elevation={3}
              sx={{ mt: 2, borderRadius: "8px" }}
            >
              <Typography variant="h6" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                Departed Employees ({governmentReport.totalCount})
              </Typography>
              <Table size="small" aria-label="government report table">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>S.No</StyledTableCell>
                    <StyledTableCell>Full Name</StyledTableCell>
                    <StyledTableCell>Passport No.</StyledTableCell>
                    <StyledTableCell>{t("country")}</StyledTableCell>
                    <StyledTableCell>Agent</StyledTableCell>
                    <StyledTableCell>Departed On</StyledTableCell>
                    <StyledTableCell align="right">
                      Reported Amount (USD)
                    </StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {governmentReport.employees.map((employee, index) => (
                    <StyledTableRow key={employee._id}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {employee.givenName} {employee.surName}
                      </StyledTableCell>
                      <StyledTableCell>
                        {employee.passportNumber}
                      </StyledTableCell>
                      <StyledTableCell>
                        {te(
                          (employee.additionalData?.appliedCountry as string) ??
                            "",
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {employee.partner?.displayName}
                      </StyledTableCell>
                      <StyledTableCell>
                        {employee.statusDates?.departed &&
                          format(
                            new Date(employee.statusDates.departed),
                            "dd-MMM-yyyy",
                          )}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {(
                          employee.agentSettlement?.reportedAmount ?? 0
                        ).toLocaleString()}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </>
  );
};
